export const DEFAULT_LOCALE = 'en-GB'
export const DEFAULT_LANG = 'en'
export const BASE_CURRENCY = 'GBP'

const EUR = { currency: 'EUR', symbol: '€', position: 'prefix', decimal: ',', monthly: '2.24', yearly: '22.49' }

const LOCALES = {
    'en-GB': { lang: 'en', currency: 'GBP', symbol: '£', position: 'prefix', decimal: '.', monthly: '1.99', yearly: '19.99' },
    'en-US': { lang: 'en', currency: 'USD', symbol: '$', position: 'prefix', decimal: '.', monthly: '2.74', yearly: '27.49' },
    'en-IE': { lang: 'en', ...EUR, decimal: '.' },
    'en-CA': { lang: 'en', currency: 'CAD', symbol: 'CA$', position: 'prefix', decimal: '.', monthly: '3.74', yearly: '37.49' },
    'en-AU': { lang: 'en', currency: 'AUD', symbol: 'A$', position: 'prefix', decimal: '.', monthly: '3.74', yearly: '37.49' },
    'en-NZ': { lang: 'en', currency: 'NZD', symbol: 'NZ$', position: 'prefix', decimal: '.', monthly: '3.99', yearly: '39.99' },
    'de-DE': { lang: 'de', ...EUR, position: 'suffix' },
    'es-ES': { lang: 'es', ...EUR, position: 'suffix' },
    'fr-FR': { lang: 'fr', ...EUR, position: 'suffix' },
    'it-IT': { lang: 'it', ...EUR, position: 'suffix' },
    'pt-PT': { lang: 'pt', ...EUR, position: 'suffix' },
    'nl-NL': { lang: 'nl', ...EUR },
    'nl-BE': { lang: 'nl', ...EUR },
    'pl-PL': { lang: 'pl', currency: 'PLN', symbol: 'zł', position: 'suffix', decimal: ',', monthly: '9.99', yearly: '99.99' },
}

// The single source of truth for which languages the site ships in. Order here
// is the order the language selector renders in.
export const LANGUAGES = [
    { code: 'en', name: 'English', locale: 'en-GB' },
    { code: 'de', name: 'Deutsch', locale: 'de-DE' },
    { code: 'es', name: 'Español', locale: 'es-ES' },
    { code: 'fr', name: 'Français', locale: 'fr-FR' },
    { code: 'it', name: 'Italiano', locale: 'it-IT' },
    { code: 'nl', name: 'Nederlands', locale: 'nl-NL' },
    { code: 'pl', name: 'Polski', locale: 'pl-PL' },
    { code: 'pt', name: 'Português', locale: 'pt-PT' },
]

const LANG_FALLBACK = Object.fromEntries(LANGUAGES.map(({ code, locale }) => [code, locale]))

export const SUPPORTED_LANGS = LANGUAGES.map(({ code }) => code)

export const NON_DEFAULT_LANGS = SUPPORTED_LANGS.filter((code) => code !== DEFAULT_LANG)

// Every page is served from <path>/index.html, so the trailing-slash form is
// the one nginx answers with a 200; the bare form redirects to it.
const withTrailingSlash = (path) => (path.endsWith('/') ? path : `${path}/`)

export const localizePath = (routePath, lang) => {
    const prefix = lang === DEFAULT_LANG ? '' : `/${lang}`
    return withTrailingSlash(`${prefix}${routePath}`.replace(/\/+$/, ''))
}

export const langFromPath = (pathname = '/') => {
    const [, candidate] = /^\/([a-z]{2})(?:\/|$)/.exec(pathname) || []
    return NON_DEFAULT_LANGS.includes(candidate) ? candidate : DEFAULT_LANG
}

export const stripLang = (pathname = '/') => {
    const lang = langFromPath(pathname)
    if (lang === DEFAULT_LANG) return pathname || '/'
    return pathname.slice(lang.length + 1) || '/'
}

export const languageName = (code) => LANGUAGES.find((l) => l.code === code)?.name || code

export const resolveLocale = (input) => {
    if (!input) return DEFAULT_LOCALE
    const [language, region] = input.split('-')
    const tag = region ? `${language.toLowerCase()}-${region.toUpperCase()}` : language.toLowerCase()
    if (LOCALES[tag]) return tag
    return LANG_FALLBACK[language.toLowerCase()] || DEFAULT_LOCALE
}

export const getLocale = (input) => {
    const tag = resolveLocale(input)
    const entry = LOCALES[tag]
    return { ...entry, tag, approx: entry.currency !== BASE_CURRENCY }
}

export const resolveLang = (input) => {
    const lang = input?.toLowerCase().split('-')[0]
    return LANG_FALLBACK[lang] ? lang : DEFAULT_LANG
}

// Non-breaking so a suffixed symbol cannot wrap away from its amount.
const NBSP = '\u00A0'

export const formatPrice = (locale, amount, { decimals = 2 } = {}) => {
    const value = (typeof amount === 'number' ? amount : parseFloat(amount)).toFixed(decimals)
    const body = value.replace('.', locale.decimal)
    const approx = locale.approx ? '~' : ''
    return locale.position === 'suffix'
        ? `${approx}${body}${NBSP}${locale.symbol}`
        : `${approx}${locale.symbol}${body}`
}

export const yearlySavingPercent = (locale) =>
    Math.round((1 - parseFloat(locale.yearly) / (parseFloat(locale.monthly) * 12)) * 100)
